import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatMessage, ChatSuggestion, ChoiceOption, SentEmailData, TriageResolvedAction } from '../../shared/types/chat'
import type { NotebookNote } from '../../shared/types/notebook'
import type { ScreenName, DeepLink } from '../../shared/types/navigation'
import type { ChatActions } from './ChatContext'
import { createNotebookEntry } from '../notebook'
import { connectGoogle } from '../../shared/api/connections'
import { formatDisplayTime } from '@tendbot/ui'
import * as api from './api'

interface UseChatCardActionsParams {
  messages: ChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  updateMessage: (messageId: string, updater: (msg: ChatMessage) => ChatMessage) => void
  handleSendMessage: (content: string, hint?: string) => Promise<void>
  onNavigate?: (screen: ScreenName, deepLink?: DeepLink) => void
  onLongPress: (messageId: string) => void
  onOpenEmail: (emailId: string) => void
}

export interface UseChatCardActionsResult {
  chatActions: ChatActions
  handleRetry: (messageId: string, retryContent: string) => void
  handleSuggestionClick: (suggestion: ChatSuggestion) => void
}

export function useChatCardActions({
  messages,
  setMessages,
  updateMessage,
  handleSendMessage,
  onNavigate,
  onLongPress,
  onOpenEmail,
}: UseChatCardActionsParams): UseChatCardActionsResult {
  const { t } = useTranslation('chat')

  const handleApproval = useCallback(async (messageId: string, approved: boolean, overrideApprovalId?: string, connectionId?: string, editedBody?: string): Promise<void> => {
    try {
      const msg = messages.find(m => m.id === messageId)
      const approvalId = overrideApprovalId ?? msg?.approval?.id
      if (!approvalId) {
        console.error('No approval ID found for message', messageId)
        return
      }

      const response = approved
        ? await api.approveAction(approvalId, 'allow_once', connectionId, editedBody)
        : await api.denyAction(approvalId)

      setMessages(prev => {
        const updated = prev.map(m => {
          if (m.id === messageId && m.approval) {
            return {
              ...m,
              approval: { ...m.approval, status: approved ? 'approved' as const : 'denied' as const }
            }
          }
          return m
        })

        if (response.reply) {
          // Build sentEmail card from approval data when an email is approved
          const approvalMsg = prev.find(m => m.id === messageId)
          const emailInfo = approvalMsg?.approval?.extendedInfo?.email
          let sentEmail: SentEmailData | undefined
          if (approved && emailInfo?.to && emailInfo?.subject) {
            sentEmail = {
              to: emailInfo.to,
              toEmail: emailInfo.to,
              subject: emailInfo.subject,
              preview: editedBody ?? emailInfo.body ?? '',
              status: 'delivered',
            }
          }

          updated.push({
            id: response.reply.messageId,
            type: 'assistant',
            content: response.reply.content,
            timestamp: response.reply.timestamp,
            displayTime: formatDisplayTime(response.reply.timestamp),
            ...(sentEmail && { sentEmail }),
          })
        }

        return updated
      })
    } catch (err) {
      console.error('Failed to update approval:', err)
    }
  }, [messages, setMessages])

  const handleRetry = useCallback((messageId: string, retryContent: string) => {
    // Remove the error message AND the last user message before it — handleSendMessage
    // will re-add the user message, so dropping the original prevents duplication.
    setMessages(prev => {
      const errorIdx = prev.findIndex(m => m.id === messageId)
      if (errorIdx === -1) return prev
      let userIdx = errorIdx - 1
      while (userIdx >= 0 && prev[userIdx]!.type !== 'user') userIdx--
      if (userIdx < 0) return prev.filter((_, i) => i !== errorIdx)
      return prev.filter((_, i) => i !== errorIdx && i !== userIdx)
    })
    handleSendMessage(retryContent)
  }, [setMessages, handleSendMessage])

  const handleSuggestionClick = useCallback((suggestion: ChatSuggestion): void => {
    const text = suggestion.category ? t(`suggestions.${suggestion.category}`, suggestion.text) : suggestion.text
    handleSendMessage(text)
  }, [handleSendMessage, t])

  const handleChoiceSelect = useCallback((messageId: string, option: ChoiceOption): void => {
    updateMessage(messageId, msg => ({ ...msg, selectedChoice: option.id }))
    if (option.approvalId) {
      handleApproval(messageId, true, option.approvalId)
      return
    }
    if (option.linkedNoteId) {
      onNavigate?.('notebook', { type: 'note', id: option.linkedNoteId })
      return
    }
    if (option.connectAction === 'google_workspace' || option.connectAction === 'google_calendar') {
      handleSendMessage(option.label, option.hint)
      connectGoogle({ features: ['calendar', 'email', 'contacts'] })
        .then(result => { window.location.href = result.authUrl })
        .catch(() => {})
      return
    }
    if (option.connectAction || /^connect\b/i.test(option.label)) {
      handleSendMessage(option.label, option.hint)
      onNavigate?.('plugins')
      return
    }
    handleSendMessage(option.label, option.hint)
  }, [updateMessage, handleApproval, handleSendMessage, onNavigate])

  const handleMultiChoiceToggle = useCallback((messageId: string, optionId: string): void => {
    updateMessage(messageId, msg => {
      const current = msg.selectedChoices || []
      const updated = current.includes(optionId)
        ? current.filter((id: string) => id !== optionId)
        : [...current, optionId]
      return { ...msg, selectedChoices: updated }
    })
  }, [updateMessage])

  const handleMultiChoiceSubmit = useCallback((messageId: string): void => {
    setMessages(prev => {
      const msg = prev.find(m => m.id === messageId)
      if (!msg?.selectedChoices?.length) return prev
      const labels = (msg.choiceOptions || [])
        .filter((opt: ChoiceOption) => msg.selectedChoices!.includes(opt.id))
        .map((opt: ChoiceOption) => opt.label)
      const updated = prev.map(m =>
        m.id === messageId ? { ...m, multiChoiceSubmitted: true } : m
      )
      handleSendMessage(labels.join(', '))
      return updated
    })
  }, [setMessages, handleSendMessage])

  const handleChecklistToggle = useCallback((messageId: string, itemId: string): void => {
    updateMessage(messageId, msg => {
      if (!msg.checklist) return msg
      return {
        ...msg,
        checklist: {
          ...msg.checklist,
          items: msg.checklist.items.map(item =>
            item.id === itemId ? { ...item, checked: !item.checked } : item
          )
        }
      }
    })
  }, [updateMessage])

  const handleChecklistDone = useCallback((messageId: string): void => {
    updateMessage(messageId, msg => {
      if (!msg.checklist) return msg
      return { ...msg, checklist: { ...msg.checklist, submitted: true } }
    })
    handleSendMessage('All packed and ready!')
  }, [updateMessage, handleSendMessage])

  const handleComparisonSelect = useCallback((messageId: string, optionId: string, optionName: string): void => {
    updateMessage(messageId, msg => {
      if (!msg.comparison) return msg
      return { ...msg, selectedComparison: optionId }
    })
    handleSendMessage(optionName)
  }, [updateMessage, handleSendMessage])

  const handleInfoAction = useCallback((_messageId: string, _actionId: string, actionLabel: string, linkedNoteId?: string): void => {
    if (linkedNoteId) {
      onNavigate?.('notebook', { type: 'note', id: linkedNoteId })
      return
    }
    handleSendMessage(actionLabel)
  }, [onNavigate, handleSendMessage])

  const handleUpdateMessage = useCallback((messageId: string, updater: (msg: ChatMessage) => ChatMessage): void => {
    updateMessage(messageId, updater)
  }, [updateMessage])

  const handleSaveToNotebook = useCallback(async (messageId: string): Promise<void> => {
    setMessages(prev => {
      const msg = prev.find(m => m.id === messageId)
      if (!msg) return prev

      let note: Partial<NotebookNote>

      if (msg.checklist) {
        const checklistMd = msg.checklist.items
          .map(item => `- [${item.checked ? 'x' : ' '}] ${item.label}`)
          .join('\n')
        note = {
          title: msg.checklist.title,
          contentMd: [msg.checklist.subtitle || msg.checklist.title, '', checklistMd].join('\n'),
          tags: [],
        }
      } else if (msg.infoCard) {
        const sections = msg.infoCard.sections?.map(s => {
          const rows = s.rows.map(r => `- **${r.label}:** ${r.value}`).join('\n')
          return s.label ? `**${s.label}**\n${rows}` : rows
        }).join('\n\n') || ''
        note = {
          title: msg.infoCard.title,
          contentMd: [msg.infoCard.subtitle, sections, msg.infoCard.note].filter(Boolean).join('\n\n'),
          tags: msg.infoCard.tags || [],
        }
      } else {
        const content = msg.content || ''
        note = {
          title: content.slice(0, 40) + (content.length > 40 ? '...' : ''),
          contentMd: content,
          tags: [],
        }
      }

      createNotebookEntry({
        title: note.title ?? '',
        contentMd: note.contentMd ?? '',
        tags: note.tags ?? [],
        icon: note.icon,
      }).then(serverNote => {
        updateMessage(messageId, m => ({ ...m, savedToNotebook: true, notebookNoteId: serverNote.id }))
      }).catch(err => {
        console.error('Failed to save to notebook:', err)
      })

      return prev
    })
  }, [setMessages, updateMessage])

  const handleTriageAction = useCallback((messageId: string, signalId: string, action: TriageResolvedAction): void => {
    // Optimistically mark as resolved
    updateMessage(messageId, msg => ({
      ...msg,
      triageEmail: msg.triageEmail ? { ...msg.triageEmail, resolvedAction: action } : msg.triageEmail,
    }))

    const apiCall = action === 'blocked'
      ? api.blockTriageSender(signalId)
      : action === 'claimed'
        ? api.claimTriageEmail(signalId)
        : api.analyzeTriageEmail(signalId)

    apiCall.catch(err => {
      console.error(`Failed triage action '${action}':`, err)
      // Revert optimistic update
      updateMessage(messageId, msg => ({
        ...msg,
        triageEmail: msg.triageEmail ? { ...msg.triageEmail, resolvedAction: undefined } : msg.triageEmail,
      }))
    })
  }, [updateMessage])

  const handleViewInNotebook = useCallback((noteId: string) => {
    onNavigate?.('notebook', { type: 'note', id: noteId })
  }, [onNavigate])

  // Optimistically remove an item from a briefing card, persist the action,
  // and restore the item on error.
  const removeBriefingItem = useCallback((
    messageId: string,
    sectionIndex: number,
    itemIndex: number,
    persist: () => Promise<unknown>,
  ): void => {
    let snapshot: ChatMessage | undefined
    updateMessage(messageId, msg => {
      snapshot = msg
      const card = msg.briefingCard
      if (!card) return msg
      const sections = card.sections.map((s, sIdx) => {
        if (sIdx !== sectionIndex) return s
        const items = s.items.filter((_, iIdx) => iIdx !== itemIndex)
        return { ...s, items }
      }).filter(s => s.items.length > 0)
      return { ...msg, briefingCard: { ...card, sections } }
    })

    persist().catch(err => {
      console.error('Briefing action failed:', err)
      if (snapshot) {
        const restore = snapshot
        updateMessage(messageId, () => restore)
      }
    })
  }, [updateMessage])

  const handleDismissBriefingItem = useCallback((
    messageId: string,
    args: { description: string; itemKey?: string; sectionIndex: number; itemIndex: number },
  ): void => {
    removeBriefingItem(
      messageId,
      args.sectionIndex,
      args.itemIndex,
      () => api.dismissBriefingItem(args.description, args.itemKey),
    )
  }, [removeBriefingItem])

  const handleResolveFollowup = useCallback((
    messageId: string,
    args: { followupId: string; sectionIndex: number; itemIndex: number },
  ): void => {
    removeBriefingItem(
      messageId,
      args.sectionIndex,
      args.itemIndex,
      () => api.resolveFollowup(args.followupId),
    )
  }, [removeBriefingItem])

  const chatActions = useMemo<ChatActions>(() => ({
    onApprove: (id: string, connectionId?: string, editedBody?: string) => handleApproval(id, true, undefined, connectionId, editedBody),
    onDeny: (id: string) => handleApproval(id, false),
    onChoiceSelect: handleChoiceSelect,
    onMultiChoiceToggle: handleMultiChoiceToggle,
    onMultiChoiceSubmit: handleMultiChoiceSubmit,
    onChecklistToggle: handleChecklistToggle,
    onChecklistDone: handleChecklistDone,
    onComparisonSelect: handleComparisonSelect,
    onInfoAction: handleInfoAction,
    onUpdateMessage: handleUpdateMessage,
    onSendMessage: handleSendMessage,
    onSaveToNotebook: handleSaveToNotebook,
    onViewInNotebook: handleViewInNotebook,
    onTriageAction: handleTriageAction,
    onNavigate: (screen: string) => onNavigate?.(screen as ScreenName),
    onLongPress,
    onDismissBriefingItem: handleDismissBriefingItem,
    onResolveFollowup: handleResolveFollowup,
    onOpenEmail,
  }), [handleApproval, handleChoiceSelect, handleMultiChoiceToggle, handleMultiChoiceSubmit, handleChecklistToggle, handleChecklistDone, handleComparisonSelect, handleInfoAction, handleUpdateMessage, handleSendMessage, handleSaveToNotebook, handleViewInNotebook, handleTriageAction, onNavigate, onLongPress, handleDismissBriefingItem, handleResolveFollowup, onOpenEmail])

  return { chatActions, handleRetry, handleSuggestionClick }
}
